import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, addDoc, getDocs, serverTimestamp, updateDoc, deleteDoc, doc } from "firebase/firestore";
import Select from "react-select";

const U = (s = "") => s.trim().toLocaleUpperCase("tr-TR");

// Makine saati takibi yapılan araç cinsleri — sadece bunlar seçilince Makine Saati alanları çıkar
const MACHINE_HOUR_CINS = ["POMPA"];
const baseRule = () => ({
  label: "", minAge: 0, maxAge: 5,
  brand: "", brandEnabled: false, modelName: "", modelEnabled: false, bomMode: "", bomMin: "", bomMax: "",
  months: 12, workHours: 1000, machineHours: 0, km: 0,
  warnDays: 30, warnHours: 100, warnMachineHours: 0, warnKm: 0
});

const VehicleDocManagement = () => {
  const [plates, setPlates] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ana Form State
  const [dtName, setDtName] = useState("");
  const [dtSelectedTypes, setDtSelectedTypes] = useState([]);
  const [dtSelectedPlates, setDtSelectedPlates] = useState([]);
  
  // Kurallar (Rules) State
  const [rules, setRules] = useState([baseRule()]);

  useEffect(() => {
    (async () => {
      try {
        const [pSnap, tSnap] = await Promise.all([
          getDocs(collection(db, "plates")),
          getDocs(collection(db, "aracDocTypes")), // Kayıtların tutulduğu koleksiyon burası
        ]);
        setPlates(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setDocTypes(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) { console.error("Hata:", error); }
      finally { setLoading(false); }
    })();
  }, []);

  const typeOptions = useMemo(() => {
    const map = new Map(); // normalize → original (ilk görülen kazanır)
    plates.forEach(p => {
      if (!p.aracCinsi) return;
      const key = p.aracCinsi.trim().toLocaleUpperCase("tr-TR");
      if (!map.has(key)) map.set(key, p.aracCinsi.trim()); // orijinal hali koru
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1], "tr"))
      .map(([, original]) => ({ value: original, label: original }));
  }, [plates]);

  const vehicleFieldOptions = useMemo(() => {
    const toOptions = (field) => Array.from(new Set(
      plates.map(p => String(p[field] || "").trim()).filter(Boolean)
    )).sort((a, b) => a.localeCompare(b, "tr")).map(v => ({ value: v, label: v }));
    return {
      brands: toOptions("aracMarka"),
      models: toOptions("aracModel")
    };
  }, [plates]);

  const selectedCinsValues = useMemo(() => dtSelectedTypes.map(o => o.value), [dtSelectedTypes]);

  const plateOptions = useMemo(() => {
    const hasCinsFilter = selectedCinsValues.length > 0;
    return plates
      .filter(p => {
        if (!p.plate) return false;
        if (p.active === false) return false;
        if (!hasCinsFilter) return true;
        return selectedCinsValues.some(c => U(c) === U(p.aracCinsi || ""));
      })
      .sort((a, b) => String(a.plate || "").localeCompare(String(b.plate || ""), "tr"))
      .map(p => ({ value: p.plate, label: `${p.plate}${p.aracCinsi ? ` - ${p.aracCinsi}` : ""}` }));
  }, [plates, selectedCinsValues]);

  useEffect(() => {
    if (!dtSelectedPlates.length) return;
    const allowed = new Set(plateOptions.map(o => o.value));
    const next = dtSelectedPlates.filter(p => allowed.has(p.value));
    if (next.length !== dtSelectedPlates.length) setDtSelectedPlates(next);
  }, [plateOptions, dtSelectedPlates]);

  const addRuleRow = () => {
    setRules([...rules, { ...baseRule(), maxAge: 0, months: 0, workHours: 0, warnDays: 15 }]);
  };

  const updateRule = (index, field, value) => {
    const newRules = [...rules];
    newRules[index][field] = value;
    setRules(newRules);
  };

  // ANA SİLME FONKSİYONU (Koleksiyondan kalıcı olarak siler)
  const deleteFullDocType = async (id) => {
    if (window.confirm("Bu kural setini ve tüm alt kurallarını tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) {
      try {
        await deleteDoc(doc(db, "aracDocTypes", id));
        setDocTypes(prev => prev.filter(t => t.id !== id));
        window.alert("Kural seti başarıyla silindi.");
      } catch (e) {
        window.alert("Silme hatası: " + e.message);
      }
    }
  };

  const saveDocType = async () => {
    if (!dtName) return window.alert("Lütfen belge adını girin.");
    if (dtSelectedTypes.length === 0 && dtSelectedPlates.length === 0) {
      return window.alert("Lütfen en az bir araç cinsi veya plaka seçin.");
    }

    const payload = {
      docName: U(dtName),
      targetCins: dtSelectedTypes.map(o => o.value), // orijinal hali koru
      targetPlates: dtSelectedPlates.map(o => o.value),
      rules: rules.map(r => ({
        label: r.label,
        condition: {
          minAge: Number(r.minAge),
          maxAge: Number(r.maxAge),
          brand: r.brand || "",
          modelName: r.modelName || "",
          bomMode: r.bomMode || "",
          bomMin: r.bomMin === "" ? null : Number(r.bomMin),
          bomMax: r.bomMax === "" ? null : Number(r.bomMax)
        },
        limits: { months: Number(r.months), workHours: Number(r.workHours), machineHours: Number(r.machineHours || 0), km: Number(r.km) },
        thresholds: { days: Number(r.warnDays), hours: Number(r.warnHours), machineHours: Number(r.warnMachineHours || 0), km: Number(r.warnKm) }
      })),
      updatedAt: serverTimestamp(),
      updatedBy: auth?.currentUser?.email || "admin"
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, "aracDocTypes", editingId), payload);
        setDocTypes(prev => prev.map(t => t.id === editingId ? { id: t.id, ...payload } : t));
      } else {
        const ref = await addDoc(collection(db, "aracDocTypes"), { ...payload, createdAt: serverTimestamp() });
        setDocTypes(prev => [...prev, { id: ref.id, ...payload }]);
      }
      resetForm();
    } catch (e) { window.alert("Hata: " + e.message); }
  };

  const resetForm = () => {
    setEditingId(null); setDtName(""); setDtSelectedTypes([]); setDtSelectedPlates([]);
    setRules([baseRule()]);
  };

  const styles = {
    card: { background: "#fff", borderRadius: "10px", padding: "24px", marginBottom: "20px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    title: { fontSize: "15px", fontWeight: "700", marginBottom: "16px", color: "#2d3748", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" },
    label: { color: "#718096", fontSize: "10px", fontWeight: "700", marginBottom: "4px", display: "block", textTransform: "uppercase", letterSpacing: "0.5px" },
    input: { padding: "7px 9px", border: "1px solid #cbd5e0", borderRadius: "5px", width: "100%", fontSize: "13px", boxSizing: "border-box" },
    btnPrimary: { background: "#2b6cb0", color: "#fff", padding: "10px 20px", borderRadius: "6px", border: "none", cursor: "pointer", fontWeight: "600" },
    btnAction: { background: "#edf2f7", border: "none", padding: "5px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" },
    btnDelete: { background: "#fff5f5", color: "#c53030", border: "none", padding: "5px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px", marginLeft: "5px" }
  };

  // Seçili cinsler arasında Makine Saati takibi yapılan var mı?
  const hasMachineHourCins = selectedCinsValues.some(c => MACHINE_HOUR_CINS.includes(U(c)));

  return (
    <div style={{ padding: "20px", maxWidth: "1500px", margin: "auto" }}>
      
      {/* 1) TANIMLAMA FORMU */}
      <div style={styles.card}>
        <div style={styles.title}>Belge Kural Seti Tanımla</div>
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          <div style={{ flex: 1 }}>
            <span style={styles.label}>Belge Adı (Örn: NDT Kontrolü)</span>
            <input style={styles.input} value={dtName} onChange={e => setDtName(e.target.value)} />
          </div>
          <div style={{ flex: 2 }}>
            <span style={styles.label}>Uygulanacak Araç Cinsleri</span>
            <Select
              isMulti
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
              options={typeOptions}
              value={dtSelectedTypes}
              onChange={setDtSelectedTypes}
              isLoading={loading}
              placeholder="Cins seçin..."
            />
          </div>
          <div style={{ flex: 2 }}>
            <span style={styles.label}>Uygulanacak Plakalar</span>
            <Select
              isMulti
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
              options={plateOptions}
              value={dtSelectedPlates}
              onChange={setDtSelectedPlates}
              isLoading={loading}
              placeholder={selectedCinsValues.length ? "Seçili cinslere ait plaka seçin..." : "Plaka seçin..."}
              noOptionsMessage={() => selectedCinsValues.length ? "Seçili cinslere ait plaka yok" : "Plaka bulunamadı"}
            />
          </div>
        </div>

        {/* Makine saati bilgi notu — sadece Pompa seçilince */}
        {hasMachineHourCins && (
          <div style={{ padding: "10px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", color: "#1e40af", fontSize: "13px", marginBottom: "16px" }}>
            ℹ️ Seçili cinsler arasında <b>{selectedCinsValues.filter(c => MACHINE_HOUR_CINS.includes(U(c))).join(", ")}</b> var. Makine Saati limiti ve uyarı alanları aktif edildi.
          </div>
        )}

        <div style={{ marginTop: "10px" }}>
          <span style={styles.label}>Geçerlilik Kuralları (Dinamik Liste)</span>

          {rules.map((rule, idx) => (
            <div key={idx} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px", marginBottom: "12px" }}>
              {/* Satır 1: Etiket, Yaş, Ay */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "12px", marginBottom: "10px" }}>
                <div>
                  <span style={styles.label}>Kural Etiketi</span>
                  <input style={styles.input} placeholder="Örn: Genç Pompa" value={rule.label} onChange={e => updateRule(idx, "label", e.target.value)} />
                </div>
                <div>
                  <span style={styles.label}>Yaş (Min — Max)</span>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input style={styles.input} type="number" placeholder="Min" value={rule.minAge} onChange={e => updateRule(idx, "minAge", e.target.value)} />
                    <input style={styles.input} type="number" placeholder="Max" value={rule.maxAge} onChange={e => updateRule(idx, "maxAge", e.target.value)} />
                  </div>
                </div>
                <div>
                  <span style={styles.label}>Ay (Geçerlilik)</span>
                  <input style={styles.input} type="number" value={rule.months} onChange={e => updateRule(idx, "months", e.target.value)} />
                </div>
              </div>

              {(rule.brandEnabled || rule.brand || rule.modelEnabled || rule.modelName || rule.bomMode) ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(180px, 1fr)) auto", gap: "12px", alignItems: "end", marginBottom: "10px", padding: "12px", background: "#fff", border: "1px dashed #cbd5e1", borderRadius: "8px" }}>
                  {(rule.brandEnabled || rule.brand) && (
                    <div>
                      <span style={styles.label}>Araç Marka</span>
                      <Select
                        isClearable
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        options={vehicleFieldOptions.brands}
                        value={rule.brand ? { value: rule.brand, label: rule.brand } : null}
                        onChange={o => updateRule(idx, "brand", o?.value || "")}
                        placeholder="İsteğe bağlı"
                      />
                    </div>
                  )}
                  {(rule.modelEnabled || rule.modelName) && (
                    <div>
                      <span style={styles.label}>Araç Modeli</span>
                      <Select
                        isClearable
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        options={vehicleFieldOptions.models}
                        value={rule.modelName ? { value: rule.modelName, label: rule.modelName } : null}
                        onChange={o => updateRule(idx, "modelName", o?.value || "")}
                        placeholder="İsteğe bağlı"
                      />
                    </div>
                  )}
                  {rule.bomMode && (
                    <div>
                      <span style={styles.label}>Bom Uzunluğu</span>
                      <div style={{ display: "grid", gridTemplateColumns: rule.bomMode === "between" ? "1fr 1fr 1fr" : "1fr 1fr", gap: "6px" }}>
                        <select style={styles.input} value={rule.bomMode} onChange={e => updateRule(idx, "bomMode", e.target.value)}>
                          <option value="gte">Büyük / Eşit</option>
                          <option value="between">Arasında</option>
                        </select>
                        <input style={styles.input} type="number" placeholder="Min" value={rule.bomMin ?? ""} onChange={e => updateRule(idx, "bomMin", e.target.value)} />
                        {rule.bomMode === "between" && (
                          <input style={styles.input} type="number" placeholder="Max" value={rule.bomMax ?? ""} onChange={e => updateRule(idx, "bomMax", e.target.value)} />
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    style={{ ...styles.btnAction, background: "#fff", border: "1px solid #cbd5e1" }}
                    onClick={() => setRules(rules.map((r, i) => i === idx ? { ...r, brand: "", brandEnabled: false, modelName: "", modelEnabled: false, bomMode: "", bomMin: "", bomMax: "" } : r))}
                  >
                    Ek Koşulları Temizle
                  </button>
                  <div style={{ gridColumn: "1 / -1", display: "flex", gap: "8px" }}>
                    {!(rule.brandEnabled || rule.brand) && <button style={styles.btnAction} onClick={() => updateRule(idx, "brandEnabled", true)}>+ Marka</button>}
                    {!(rule.modelEnabled || rule.modelName) && <button style={styles.btnAction} onClick={() => updateRule(idx, "modelEnabled", true)}>+ Model</button>}
                    {!rule.bomMode && <button style={styles.btnAction} onClick={() => updateRule(idx, "bomMode", "gte")}>+ Bom Uzunluğu</button>}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                  <button style={styles.btnAction} onClick={() => updateRule(idx, "brandEnabled", true)}>+ Marka</button>
                  <button style={styles.btnAction} onClick={() => updateRule(idx, "modelEnabled", true)}>+ Model</button>
                  <button style={styles.btnAction} onClick={() => updateRule(idx, "bomMode", "gte")}>+ Bom Uzunluğu</button>
                </div>
              )}

              {/* Satır 2: Limitler */}
              <div style={{ display: "grid", gridTemplateColumns: hasMachineHourCins ? "1fr 1fr 1fr" : "1fr 1fr", gap: "12px", marginBottom: "10px" }}>
                <div>
                  <span style={styles.label}>Araç Saati Limiti</span>
                  <input style={styles.input} type="number" placeholder="0 = sorgulanmaz" value={rule.workHours} onChange={e => updateRule(idx, "workHours", e.target.value)} />
                </div>
                {hasMachineHourCins && (
                  <div>
                    <span style={styles.label}>Makine Saati Limiti</span>
                    <input style={styles.input} type="number" placeholder="0 = sorgulanmaz" value={rule.machineHours ?? ""} onChange={e => updateRule(idx, "machineHours", e.target.value)} />
                  </div>
                )}
                <div>
                  <span style={styles.label}>KM Limiti</span>
                  <input style={styles.input} type="number" placeholder="0 = sorgulanmaz" value={rule.km} onChange={e => updateRule(idx, "km", e.target.value)} />
                </div>
              </div>

              {/* Satır 3: Uyarı eşikleri */}
              <div style={{ display: "grid", gridTemplateColumns: hasMachineHourCins ? "1fr 1fr 1fr 1fr auto" : "1fr 1fr 1fr auto", gap: "12px", alignItems: "end" }}>
                <div>
                  <span style={styles.label}>Uyarı Günü</span>
                  <input style={styles.input} type="number" value={rule.warnDays} onChange={e => updateRule(idx, "warnDays", e.target.value)} />
                </div>
                <div>
                  <span style={styles.label}>Uyarı Saati</span>
                  <input style={styles.input} type="number" value={rule.warnHours} onChange={e => updateRule(idx, "warnHours", e.target.value)} />
                </div>
                {hasMachineHourCins && (
                  <div>
                    <span style={styles.label}>Uyarı Mak. Saati</span>
                    <input style={styles.input} type="number" value={rule.warnMachineHours ?? ""} onChange={e => updateRule(idx, "warnMachineHours", e.target.value)} />
                  </div>
                )}
                <div>
                  <span style={styles.label}>Uyarı KM</span>
                  <input style={styles.input} type="number" value={rule.warnKm} onChange={e => updateRule(idx, "warnKm", e.target.value)} />
                </div>
                <button style={{ ...styles.btnAction, background: "#fff5f5", color: "#c53030", whiteSpace: "nowrap" }} onClick={() => setRules(rules.filter((_, i) => i !== idx))}>Kuralı Sil</button>
              </div>
            </div>
          ))}
          <button style={{ ...styles.btnAction, marginTop: "4px" }} onClick={addRuleRow}>+ Yeni Kural Satırı Ekle</button>
        </div>

        <div style={{ marginTop: "24px", textAlign: "right" }}>
          <button style={styles.btnPrimary} onClick={saveDocType}>{editingId ? "Değişiklikleri Güncelle" : "Kural Setini Kaydet"}</button>
          {editingId && <button style={{ marginLeft: "10px", ...styles.btnAction }} onClick={resetForm}>Vazgeç / İptal</button>}
        </div>
      </div>

      {/* 2) LİSTELEME VE SİLME */}
      <div style={styles.card}>
        <div style={styles.title}>Tanımlı Kural Setleri</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", background: "#f7fafc", fontSize: "12px" }}>
              <th style={{ padding: "12px" }}>Belge</th>
              <th style={{ padding: "12px" }}>Araç Cinsleri</th>
              <th style={{ padding: "12px" }}>Plakalar</th>
              <th style={{ padding: "12px" }}>Kurallar</th>
              <th style={{ padding: "12px", textAlign: "right" }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {docTypes.sort((a,b) => (a.docName || "").localeCompare(b.docName || "")).map(t => (
              <tr key={t.id} style={{ borderBottom: "1px solid #edf2f7", fontSize: "13px" }}>
                <td style={{ padding: "12px", fontWeight: "600" }}>{t.docName}</td>
                <td style={{ padding: "12px" }}>{t.targetCins?.length ? t.targetCins.join(", ") : "-"}</td>
                <td style={{ padding: "12px" }}>{t.targetPlates?.length ? t.targetPlates.join(", ") : "-"}</td>
                <td style={{ padding: "12px" }}>
                  {t.rules?.map((r, i) => (
                    <div key={i} style={{ fontSize: "11px", color: "#4a5568", marginBottom: "2px" }}>
                      • {r.label}: {r.condition?.minAge}-{r.condition?.maxAge} Yaş | {r.limits?.months} Ay
                      {r.limits?.workHours > 0 && ` | ${r.limits.workHours} Araç Saat`}
                      {r.limits?.machineHours > 0 && ` | ${r.limits.machineHours} Mak. Saat`}
                      {r.limits?.km > 0 && ` | ${r.limits.km} KM`}
                    </div>
                  ))}
                </td>
                <td style={{ padding: "12px", textAlign: "right" }}>
                  <button style={styles.btnAction} onClick={() => {
                    setEditingId(t.id); setDtName(t.docName);
                    setDtSelectedTypes((t.targetCins || []).map(c => ({ value: c, label: c })));
                    setDtSelectedPlates((t.targetPlates || []).map(p => ({ value: p, label: p })));
                    setRules(t.rules.map(r => ({
                      label: r.label, minAge: r.condition?.minAge, maxAge: r.condition?.maxAge,
                      brand: r.condition?.brand || "", brandEnabled: Boolean(r.condition?.brand),
                      modelName: r.condition?.modelName || "", modelEnabled: Boolean(r.condition?.modelName),
                      bomMode: r.condition?.bomMode || "", bomMin: r.condition?.bomMin ?? "", bomMax: r.condition?.bomMax ?? "",
                      months: r.limits?.months, workHours: r.limits?.workHours,
                      machineHours: r.limits?.machineHours || 0, km: r.limits?.km,
                      warnDays: r.thresholds?.days, warnHours: r.thresholds?.hours,
                      warnMachineHours: r.thresholds?.machineHours || 0, warnKm: r.thresholds?.km
                    })));
                    window.scrollTo(0,0);
                  }}>Düzenle</button>
                  <button style={styles.btnDelete} onClick={() => deleteFullDocType(t.id)}>Sil</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VehicleDocManagement;
